import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { scannerAPI, adminAPI, authAPI } from '../services/api';
import { removeToken } from '../utils/auth';
import ScannerList from '../components/ScannerList';
import ScannerForm from '../components/ScannerForm';
import WebhookTestResultModal from '../components/WebhookTestResultModal';
import LastScanResults from '../components/LastScanResults';
import './Dashboard.css';

function Dashboard() {
    const [scanners, setScanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [userName, setUserName] = useState('');
    const [webhookTestResult, setWebhookTestResult] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadUserInfo();
        loadScanners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadUserInfo = async () => {
        try {
            const response = await authAPI.getCurrentUser();
            const user = response.data.data.user;
            setUserRole(user.role);
            setUserName(user.name);
        } catch (err) {
            console.error('Failed to load user info:', err);
        }
    };

    const loadScanners = async () => {
        try {
            setLoading(true);
            // Check if token has user info to determine role
            const userResponse = await authAPI.getCurrentUser();
            const user = userResponse.data.data.user;

            let response;
            if (user.role === 'ADMIN') {
                response = await adminAPI.getAllScanners();
            } else {
                response = await scannerAPI.getScanners();
            }

            const scanners = response.data.data.scanners;
            setScanners(scanners);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load scanners');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        removeToken();
        navigate('/login');
    };

    const handleCreateScanner = async (scannerData) => {
        try {
            if (userRole === 'ADMIN') {
                await adminAPI.createScanner(scannerData);
            } else {
                throw new Error('Only admins can create scanners');
            }
            setShowCreateForm(false);
            loadScanners();
        } catch (err) {
            throw err;
        }
    };

    const handleTestWebhook = async (scannerId, testType) => {
        try {
            const response = await scannerAPI.sendTestWebhook(scannerId, testType);
            const data = response.data.data;

            if (data) {
                // Show the result in the modal
                setWebhookTestResult(data);
            } else {
                alert(response.data.message);
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to send test webhook');
        }
    };

    return (
        <div className="dashboard">
            <header className="dashboard-header">
                <div>
                    <h1>hVerify ID Scanner Management</h1>
                    {userName && (
                        <p className="user-info">
                            {userName} {userRole && <span className="badge">{userRole}</span>}
                        </p>
                    )}
                </div>
                <div className="header-actions">
                    <button onClick={handleLogout} className="logout-btn">
                        Logout
                    </button>
                </div>
            </header>

            <nav className="dashboard-nav">
                <button
                    onClick={() => navigate('/')}
                    className="nav-btn active"
                >
                    ID Scanners
                </button>
                <button
                    onClick={() => navigate('/webhooks')}
                    className="nav-btn"
                >
                    My Webhooks
                </button>
                <button
                    onClick={() => navigate('/security-policies')}
                    className="nav-btn"
                >
                    Security Policies
                </button>
                <button
                    onClick={() => navigate('/operational-log')}
                    className="nav-btn"
                >
                    Operational Log
                </button>
            </nav>

            <div className="dashboard-content">
                <div className="page-header">
                    <h2>ID Scanners</h2>
                    <p>Manage your document scanners and their configurations</p>
                </div>

                {userRole === 'ADMIN' && (
                    <div className="dashboard-actions">
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="primary-btn"
                        >
                            + Add Scanner
                        </button>
                    </div>
                )}

                <LastScanResults />

                {error && <div className="error">{error}</div>}

                {showCreateForm && (
                    <ScannerForm
                        onSubmit={handleCreateScanner}
                        onCancel={() => setShowCreateForm(false)}
                    />
                )}

                {loading ? (
                    <div className="loading">Loading scanners...</div>
                ) : (
                    <ScannerList
                        scanners={scanners}
                        onScannerUpdated={loadScanners}
                        onTestWebhook={handleTestWebhook}
                    />
                )}
            </div>

            {webhookTestResult && (
                <WebhookTestResultModal
                    result={webhookTestResult}
                    onClose={() => setWebhookTestResult(null)}
                />
            )}
        </div>
    );
}

export default Dashboard;
